import type { NodeOverlay } from './canvasTypes'
import type { GraphLiveAppearance } from './graphInstrumentation'
import { hitTestClientPoint } from './nodeOverlayInspection'
import { resolvePeerOffPageConnector } from './offPageConnectorGraph'
import type { OpenOffPageConnector } from './useOpenOffPageConnector'

export type GraphNodeActivation = {
  allowsModifiers: boolean
  hitsLiveValues: boolean
  canActivate: (overlay: NodeOverlay) => boolean
  activate: (overlay: NodeOverlay, event: MouseEvent) => void
}

const clickSlop = 5

export function graphNodeActivationPolicy({
  onSelectFlowNode,
  onOpenOffPageConnector,
  onSelectNode,
}: {
  onSelectFlowNode?: (overlay: NodeOverlay) => void
  onOpenOffPageConnector?: OpenOffPageConnector
  onSelectNode: (overlay: NodeOverlay) => void
}): GraphNodeActivation {
  if (onSelectFlowNode)
    return {
      allowsModifiers: true,
      hitsLiveValues: true,
      canActivate: (): boolean => true,
      activate: (overlay): void => onSelectFlowNode(overlay),
    }
  return {
    allowsModifiers: false,
    hitsLiveValues: false,
    canActivate: (): boolean => true,
    activate: (overlay, event): void => {
      const peer = resolvePeerOffPageConnector(overlay.node)
      if (peer && onOpenOffPageConnector)
        onOpenOffPageConnector(peer, event.metaKey || event.ctrlKey)
      else onSelectNode(overlay)
    },
  }
}

/* eslint-disable lingui/no-unlocalized-strings -- DOM selectors are implementation identifiers. */
function isControl(event: MouseEvent): boolean {
  return (
    event.target instanceof Element &&
    (event.target.closest('[data-graph-tooltip]') !== null ||
      (event.target.closest('[data-graph-overlay]') === null &&
        event.target.closest(
          'button, a, input, select, textarea, [role="button"]'
        ) !== null))
  )
}
/* eslint-enable lingui/no-unlocalized-strings */

function movedBeyondClick(
  event: MouseEvent,
  start: { x: number; y: number }
): boolean {
  return (
    Math.hypot(event.clientX - start.x, event.clientY - start.y) > clickSlop
  )
}

export function listenForGraphNodePointerActivation(
  container: HTMLDivElement,
  {
    overlays,
    getSvg,
    activation,
    liveValueAppearances,
  }: {
    overlays: NodeOverlay[]
    getSvg: () => SVGSVGElement | null
    activation: GraphNodeActivation
    liveValueAppearances: GraphLiveAppearance[]
  }
): () => void {
  let start: { x: number; y: number; overlayId: string } | undefined
  const clear = (): void => {
    start = undefined
  }
  const hitAt = (event: MouseEvent): NodeOverlay | undefined =>
    hitTestClientPoint(
      overlays,
      getSvg(),
      { x: event.clientX, y: event.clientY },
      activation.hitsLiveValues ? liveValueAppearances : []
    )?.overlay
  const ignored = (event: MouseEvent): boolean =>
    event.button !== 0 ||
    (!activation.allowsModifiers && (event.shiftKey || event.altKey)) ||
    isControl(event)
  const down = (event: PointerEvent): void => {
    clear()
    if (ignored(event) || event.isPrimary === false) return
    const overlay = hitAt(event)
    if (overlay && activation.canActivate(overlay))
      start = {
        x: event.clientX,
        y: event.clientY,
        overlayId: overlay.overlayId,
      }
  }
  const move = (event: PointerEvent): void => {
    if (start && movedBeyondClick(event, start)) clear()
  }
  const click = (event: MouseEvent): void => {
    const candidate = start
    clear()
    if (!candidate || ignored(event) || movedBeyondClick(event, candidate))
      return
    const overlay = hitAt(event)
    if (
      overlay?.overlayId !== candidate.overlayId ||
      !activation.canActivate(overlay)
    )
      return
    event.preventDefault()
    event.stopPropagation()
    activation.activate(overlay, event)
  }
  container.addEventListener('pointerdown', down, true)
  container.addEventListener('pointermove', move, true)
  container.addEventListener('pointercancel', clear, true)
  container.addEventListener('pointerleave', clear)
  container.addEventListener('wheel', clear, { passive: true, capture: true })
  container.addEventListener('click', click, true)
  return (): void => {
    container.removeEventListener('pointerdown', down, true)
    container.removeEventListener('pointermove', move, true)
    container.removeEventListener('pointercancel', clear, true)
    container.removeEventListener('pointerleave', clear)
    container.removeEventListener('wheel', clear, true)
    container.removeEventListener('click', click, true)
  }
}
